import React, { useCallback } from 'react'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { SECOND_MS } from 'data/constants'
import { GetSigner } from 'privy/WalletReady'
import { SignerUndefinedError, useInitiateWithdrawTransaction } from 'use-towns-client'

export type InititateWithdrawArgs = {
    depositIds: bigint[]
    getSigner: GetSigner
}

export const useInitiateWithdraw = () => {
    const { initiateWithdraw, ...transactionRest } = useInitiateWithdrawTransaction()

    return {
        ...transactionRest,
        initiateWithdraw: useCallback(
            async (args: InititateWithdrawArgs) => {
                const { depositIds, getSigner } = args

                const signer = await getSigner()
                if (!signer) {
                    throw new SignerUndefinedError()
                }

                toasts.submitting()

                const txnContext = await initiateWithdraw({
                    depositIds,
                    signer,
                    onError: (error) => {
                        toasts.error(error)
                    },
                    onSuccess: () => {
                        toasts.success()
                    },
                })

                return txnContext
            },
            [initiateWithdraw],
        ),
    }
}

const toasts = {
    submitting: () => {
        popupToast(
            ({ toast }) => <StandardToast.Pending toast={toast} message="Initiating withdraw..." />,
            {
                id: 'initiate-withdraw',
                duration: Number.POSITIVE_INFINITY,
            },
        )
    },

    success: () => {
        popupToast(
            ({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Successfully initiated withdraw!"
                    subMessage="The withdraw has been initiated."
                />
            ),
            { id: 'initiate-withdraw', duration: SECOND_MS * 4 },
        )
    },

    error: (error: Error) => {
        console.error(error.message)
        popupToast(
            ({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Initiate withdraw failed"
                    subMessage={error.message}
                />
            ),
            { id: 'initiate-withdraw', duration: SECOND_MS * 5 },
        )
    },
}
